import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FormControl,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { interval, Subscription, takeUntil, timer } from 'rxjs';
import { TooManyTriesException } from 'src/app/core/services/authentication/authentication-errors';
import { AuthenticationService } from 'src/app/core/services/authentication/authentication.service';
import { RegisterService } from 'src/app/core/services/register/register.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import {
  NotAuthorizedException,
  UserNotConfirmedException,
} from 'src/app/shared/errors/cognito/cognito-errors';
import { LocalStorageKeys } from 'src/app/shared/models/constants/storage.keys';
import { UserStatus } from 'src/app/shared/models/user/user-status';
import { millisecondToSecond, timeLeft } from 'src/app/utils/date';
import { RoutesKeys } from 'src/app/utils/routes';
import { RecaptchaComponent } from 'src/app/shared/components/molecules/recaptcha/recaptcha.component';
import { environment } from '../../../environments/environment';

@Component({
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
})
/**
 * Shall instantiate Login template
 */
export class LoginPage implements OnInit, OnDestroy {
  loginFormGroup!: UntypedFormGroup;
  loginErrorMsg?: string = '';
  blockedLoginTimeLeft = 0;
  blockedLoginSecondsLeft = 0;
  disableLogin = false;
  loggedInRoute = '/register';
  loading = false;
  loginTimeout = 10000; // ten seconds
  RoutesKeys = RoutesKeys;
  private subsink = new Subscription();
  private redirectUrl?: string;
  errorMessage = {
    invalidEmailPass: 'invalid-email-password',
  };
  @ViewChild('captchaRef') captchaRef?: RecaptchaComponent;
  recaptchaEnabled = environment.recaptcha?.enabled ?? true;
  /**
   * @constructor
   */
  constructor(
    private authenticationService: AuthenticationService,
    private registerService: RegisterService,
    private router: Router,
    private route: ActivatedRoute,
    private translationService: TranslationService
  ) {}

  /**
   * Sets up form group
   */
  ngOnInit() {
    this.translationService.translateObject(this.errorMessage);
    this.loginFormGroup = new UntypedFormGroup({
      email: new UntypedFormControl(
        this.authenticationService.passwordResetEmail,
        [Validators.required, Validators.email]
      ),
      password: new UntypedFormControl('', [
        Validators.required,
        Validators.pattern(''),
      ]),
    });

    if (this.recaptchaEnabled) {
      this.loginFormGroup.addControl(
        'recaptcha',
        new FormControl('', Validators.required)
      );
    }

    if (
      this.authenticationService.passwordResetEmail &&
      this.registerService.invitedRepresentativeEmail ===
        this.authenticationService.passwordResetEmail
    ) {
      this.loginFormGroup.controls['email'].disable();
    }

    const inputtedEmail = this.loginFormGroup.get('email')!.value;

    if (inputtedEmail) {
      this.authenticationService.passwordResetEmail = inputtedEmail;
      this.registerService.businessRepresentativeFormGroup
        ?.get('email')
        ?.setValue(inputtedEmail.trim());
    }

    this.subsink.add(
      this.loginFormGroup
        .get('email')!
        .valueChanges.subscribe((value: string) => {
          this.authenticationService.passwordResetEmail = value;
          this.registerService.businessRepresentativeFormGroup
            ?.get('email')
            ?.setValue(value.trim());
        })
    );

    this.subsink.add(
      this.route.queryParamMap.subscribe((params) => {
        const redirectUrl = params.get('redirectTo');
        if (redirectUrl) {
          this.redirectUrl = redirectUrl;
        }
      })
    );
  }

  /**
   * Cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * @param {string} controlName
   * @return {FormControl}
   */
  getControl(controlName: string): FormControl {
    return this.loginFormGroup.get(controlName) as FormControl;
  }

  /**
   * Action to take when submitting login form
   */
  async onSubmit() {
    const { email, password } = this.loginFormGroup.getRawValue();
    const recaptcha = this.recaptchaEnabled
      ? this.loginFormGroup.get('recaptcha')?.value
      : 'dev-bypass';
    if (!email || !password) {
      throw new Error(this.errorMessage.invalidEmailPass);
    }
    this.startLoading();
    try {
      const user = await this.authenticationService.logIn(
        email,
        password,
        recaptcha
      );
      this.stopLoading();
      switch (user.status) {
        case UserStatus.LOGIN_COMPLETE:
          localStorage.setItem(
            LocalStorageKeys.NOTIFIED_ON_LOGIN,
            JSON.stringify(false)
          );
          this.authenticationService.passwordResetEmail = '';
          if (this.redirectUrl) {
            this.router
              .navigate([this.redirectUrl])
              .catch(() => this.router.navigate([RoutesKeys.HOME]));
          } else {
            this.router.navigate([RoutesKeys.HOME]);
          }
          break;
        case UserStatus.MISSING_EMAIL_VALIDATION:
          this.router.navigate([RoutesKeys.VERIFICATION]);
          break;
        case UserStatus.NEW_PASSWORD_REQUIRED:
          this.router.navigate([RoutesKeys.VERIFICATION]); // TODO Go to change password page
          break;
        default:
          // TODO Unknown status, need to redirect to error page
          this.router.navigate([RoutesKeys.LOGIN]);
          break;
      }
    } catch (error) {
      this.stopLoading();
      this.handleLogInError(error as Error);
    } finally {
      this.captchaRef?.reset();
    }
  }

  /**
   * Counts down blocked login time
   */
  private countdownBlockedTimer() {
    const oneSecond = 1000;
    const source = interval(oneSecond);
    const timer$ = timer(this.blockedLoginTimeLeft - oneSecond);
    const stopwatch = source.pipe(takeUntil(timer$));
    stopwatch.subscribe({
      next: () => {
        this.blockedLoginTimeLeft = this.blockedLoginTimeLeft - oneSecond;
        this.blockedLoginSecondsLeft = millisecondToSecond(
          this.blockedLoginTimeLeft
        );
        this.loginErrorMsg = 'too-many-failed-login-attempts';
      },
      complete: () => {
        this.loginErrorMsg = '';
        this.disableLogin = false;
      },
    });
  }

  /**
   * Calculates how much time is left until login unblock
   * @param {number} totalTime must be in milliseconds
   * @return {number} returns time left in milliseconds
   */
  private calculateBlockedLoginTimeLeft(totalTime: number): number {
    return timeLeft(
      totalTime,
      JSON.parse(localStorage.getItem(LocalStorageKeys.LOGIN_ATTEMPTS) || '{}')
        .timestamp
    );
  }

  /**
   * Sets up blocked login timer logic,
   * to be used in a too many failed login attempts feedback from backend
   * @param {number} seconds in milliseconds
   */
  private tooManyFailedLoginAttempts(seconds: number) {
    this.blockedLoginTimeLeft = this.calculateBlockedLoginTimeLeft(seconds);

    this.blockedLoginSecondsLeft = millisecondToSecond(
      this.blockedLoginTimeLeft
    );

    this.loginErrorMsg = 'too-many-failed-login-attempts';
    this.disableLogin = true;

    this.countdownBlockedTimer();
  }

  /**
   * helper function that stops loading animation related interaction
   */
  private startLoading() {
    this.loading = true;
    this.disableLogin = true;
  }

  /**
   * helper function that stops loading animation related interaction
   */
  private stopLoading() {
    this.loading = false;
    this.disableLogin = false;
  }

  /**
   * Handles possible errors in log in
   * @param {Error} error
   */
  private handleLogInError(error: Error) {
    if (error instanceof TooManyTriesException) {
      this.tooManyFailedLoginAttempts(this.loginTimeout);
    } else if (error instanceof NotAuthorizedException) {
      this.loginErrorMsg = 'invalid-email-password';
      if (error.message.startsWith('User is disabled')) {
        this.loginErrorMsg = 'user-disabled';
      }
    } else if (error instanceof UserNotConfirmedException) {
      this.router.navigate([RoutesKeys.VERIFICATION]);
    } else {
      // communication error, do not count as failed attempt
      this.loginErrorMsg = 'backend-comm-error';
    }
  }
}
